// src/components/modals/NotificationModal.tsx
// Reusable modal component to replace browser native dialogs (alert, confirm)
import React from "react";
import "./NotificationModal.css";

export type NotificationModalType = "info" | "success" | "error" | "warning" | "confirm";

interface NotificationModalProps {
  isOpen: boolean;
  type: NotificationModalType;
  title: string;
  message: string | React.ReactNode;
  onClose: () => void;
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
}

export const NotificationModal: React.FC<NotificationModalProps> = ({
  isOpen,
  type,
  title,
  message,
  onClose,
  onConfirm,
  confirmText = "OK",
  cancelText = "Cancel",
  isLoading = false,
}) => {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case "success":
        return "✓";
      case "error":
        return "✕";
      case "warning":
        return "⚠";
      case "confirm":
        return "?";
      default:
        return "ℹ";
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    } else {
      onClose();
    }
  };

  return (
    <div className="notification-modal-overlay" onClick={handleOverlayClick}>
      <div className={`notification-modal notification-modal-${type}`}>
        <div className={`notification-modal-icon notification-modal-icon-${type}`}>
          {getIcon()}
        </div>
        <h3 className="notification-modal-title">{title}</h3>
        <div className="notification-modal-message">{message}</div>
        <div className="notification-modal-actions">
          {type === "confirm" ? (
            <>
              <button
                className="notification-modal-btn notification-modal-btn-cancel"
                onClick={onClose}
                disabled={isLoading}
              >
                {cancelText}
              </button>
              <button
                className="notification-modal-btn notification-modal-btn-confirm"
                onClick={handleConfirm}
                disabled={isLoading}
              >
                {isLoading ? "..." : confirmText}
              </button>
            </>
          ) : (
            <button
              className={`notification-modal-btn notification-modal-btn-${type}`}
              onClick={onClose}
            >
              {confirmText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Hook for managing notification modal state
export interface NotificationState {
  isOpen: boolean;
  type: NotificationModalType;
  title: string;
  message: string | React.ReactNode;
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
}

export const initialNotificationState: NotificationState = {
  isOpen: false,
  type: "info",
  title: "",
  message: "",
};

export default NotificationModal;
